import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputBase,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import sh from "bundles/common/utils/sh";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import DateTimeSelect from "bundles/Internal/components/common/DateTimeSelect";

const TIME_ENTRY_CHANGED_EVENT = "time-entry:changed";

function pad(n) { return String(n).padStart(2, "0"); }

function isoLocal(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const selectSx = {
  width: "100%", height: 40, 
  fontFamily: "Segoe UI Regular, sans-serif, system-ui", 
  fontSize: 14,
  bgcolor: "hsl(140 10% 95%)",
  borderRadius: "12px",
  color: "hsl(150 30% 12%)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(150 38% 17%)" },
  "&.Mui-focused": { bgcolor: "#fff" },
};

const dialogInputSx = {
  width: "100%", height: 40, px: 1.5,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui", 
  fontSize: 14,
  bgcolor: "hsl(140 10% 95%)",
  border: "1px solid hsl(140 8% 88%)",
  borderRadius: "12px",
  color: "hsl(150 30% 12%)",
  outline: "none", boxSizing: "border-box",
  "&:focus": { borderColor: "hsl(150 38% 17%)", bgcolor: "#fff" },
};

const primaryButtonSx = {
  height: 40, borderRadius: "12px", textTransform: "none",
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 14,
  bgcolor: "hsl(150 38% 17%)", color: "hsl(80 97% 72%)",
  boxShadow: "none",
  "&:hover": { bgcolor: "hsla(150, 38%, 17%, 0.9)", boxShadow: "none" },
  "&.Mui-disabled": { opacity: 0.5, bgcolor: "hsl(150 38% 17%)", color: "hsl(80 97% 72%)" },
};

const ghostButtonSx = {
  height: 40, borderRadius: "12px", textTransform: "none",
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 14,
  color: "hsl(150 10% 46%)",
  "&:hover": { bgcolor: "hsla(140, 8%, 90%, 0.5)" },
};

const dialogLabelSx = {
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 12,
  color: "hsl(150 10% 46%)", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.04em",
};

export default function CreateTimeEntryModal({
  open,
  onClose,
  date,
  defaultHour,
  entry,
  projects: projectsProp,
  authenticityToken,
  onSaved,
}) {
  const editing = !!entry;
  const [internalProjects, setInternalProjects] = useState([]);
  const projects = projectsProp || internalProjects;

  const [projectId, setProjectId] = useState("");
  const [taskId, setTaskId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!open || projectsProp) return;
    sh.get("/internal/time_projects?caller=create-time-entry-modal")
      .then((res) => setInternalProjects(Array.isArray(res.data) ? res.data : []));
  }, [open, projectsProp]);

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setConfirmingDelete(false);
    if (entry) {
      setProjectId(entry.project?.id || "");
      setTaskId(entry.task?.id || "");
      setNote(entry.note || "");
      const startD = entry.started_at ? new Date(entry.started_at) : new Date();
      const endD = entry.ended_at ? new Date(entry.ended_at) : new Date(startD.getTime() + 3600000);
      setStart(isoLocal(startD));
      setEnd(isoLocal(endD));
      return;
    }
    const baseDate = date ? new Date(date) : new Date();
    const startD = new Date(baseDate);
    if (typeof defaultHour === "number") startD.setHours(defaultHour, 0, 0, 0);
    const endD = new Date(startD);
    endD.setHours(endD.getHours() + 1);
    setStart(isoLocal(startD));
    setEnd(isoLocal(endD));
    setProjectId("");
    setTaskId("");
    setNote("");
  }, [open, date, defaultHour, entry]);

  const project = projects.find((p) => p.id === projectId) || null;
  const startedAt = start ? new Date(start) : null;
  const endedAt = end ? new Date(end) : null;
  const validTimes = !!startedAt && !!endedAt && endedAt.getTime() > startedAt.getTime();
  const canSubmit = !!projectId && validTimes && !submitting;

  const broadcast = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(TIME_ENTRY_CHANGED_EVENT));
    }
  };

  const submit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const payload = {
      authenticity_token: authenticityToken,
      time_entry: {
        time_project_id: projectId,
        time_task_id: taskId || null,
        note: note || "",
        started_at: startedAt.toISOString(),
        ended_at:   endedAt.toISOString(),
      },
    };
    const req = editing
      ? sh.put(`/internal/time_entries/${entry.id}`, payload)
      : sh.post("/internal/time_entries", payload);
    req.then(() => {
      onSaved?.();
      broadcast();
      onClose();
    }).catch(() => {
      setSubmitting(false);
    });
  };

  const remove = () => {
    if (!editing || submitting) return;
    setSubmitting(true);
    sh.delete(`/internal/time_entries/${entry.id}`, {
      data: { authenticity_token: authenticityToken },
    }).then(() => {
      onSaved?.();
      broadcast();
      onClose();
    }).catch(() => {
      setSubmitting(false);
      setConfirmingDelete(false);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: "16px", minWidth: 420, maxWidth: 480 } }}
    >
      <DialogTitle sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontWeight: 700, fontSize: 18, color: "hsl(150 30% 12%)", pt: 3, px: 3, pb: 1 }}>
        {editing ? "Edit time entry" : "Add time entry"}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1, pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <Typography sx={dialogLabelSx}>Project</Typography>
          <Select
            value={projectId}
            onChange={(e) => { setProjectId(e.target.value); setTaskId(""); }}
            displayEmpty size="small" variant="outlined" sx={selectSx}
            renderValue={(value) => {
              if (!value) {
                return <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 10% 46%)" }}>Select project</Typography>;
              }
              const p = projects.find((pp) => pp.id === value);
              return (
                <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 30% 12%)" }}>
                  {p?.name || ""}{p?.company?.name ? ` — ${p.company.name}` : ""}
                </Typography>
              );
            }}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 30% 12%)" }}>
                  {p.name}{p.company?.name ? ` — ${p.company.name}` : ""}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box>
          <Typography sx={dialogLabelSx}>Task</Typography>
          <Select
            value={taskId}
            onChange={(e) => setTaskId(e.target.value)}
            displayEmpty size="small" variant="outlined" sx={selectSx}
            disabled={!projectId}
            renderValue={(value) => {
              if (!value) {
                return <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 10% 46%)" }}>No task</Typography>;
              }
              const t = (project?.tasks || []).find((tt) => tt.id === value);
              return (
                <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 30% 12%)" }}>
                  {t?.name?.replace(/_/g, " ") || ""}
                </Typography>
              );
            }}
          >
            <MenuItem value="">
              <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 10% 46%)" }}>No task</Typography>
            </MenuItem>
            {(project?.tasks || []).map((t) => (
              <MenuItem key={t.id} value={t.id}>
                <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 30% 12%)" }}>
                  {t.name.replace(/_/g, " ")}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={dialogLabelSx}>Starts</Typography>
            <DateTimeSelect value={start} onChange={setStart} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={dialogLabelSx}>Ends</Typography>
            <DateTimeSelect value={end} onChange={setEnd} />
          </Box>
        </Box>

        {!validTimes && start && end && (
          <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 12, color: "hsl(0 70% 45%)" }}>
            End must be after start.
          </Typography>
        )}

        <Box>
          <Typography sx={dialogLabelSx}>Note (optional)</Typography>
          <InputBase
            multiline minRows={2} maxRows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What did you work on?"
            sx={{
              width: "100%", px: 1.5, py: 1,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14,
              bgcolor: "hsl(140 10% 95%)",
              border: "1px solid hsl(140 8% 88%)",
              borderRadius: "12px",
              color: "hsl(150 30% 12%)",
              alignItems: "flex-start",
              "&.Mui-focused": { borderColor: "hsl(150 38% 17%)", bgcolor: "#fff" },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1, justifyContent: editing ? "space-between" : "flex-end" }}>
        {editing && (
          <Button
            onClick={() => setConfirmingDelete(true)}
            disabled={submitting}
            sx={{ ...ghostButtonSx, color: "hsl(0 70% 45%)", "&:hover": { bgcolor: "hsla(0, 70%, 45%, 0.08)" } }}
          >
            Delete
          </Button>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} sx={ghostButtonSx}>Cancel</Button>
          <Button
            onClick={submit}
            variant="contained"
            disableElevation
            disabled={!canSubmit}
            sx={primaryButtonSx}
          >
            {editing ? "Save changes" : "Add entry"}
          </Button>
        </Box>
      </DialogActions>
      <ConfirmDialog
        open={confirmingDelete}
        onClose={() => { if (!submitting) setConfirmingDelete(false); }}
        onConfirm={remove}
        title="Delete this time entry?"
        description="This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
      />
    </Dialog>
  );
}
